import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ExpenseCategorySummary,
  ExpenseDetail,
  ExpensePaymentMethod,
  ExpenseStatus,
  ExpenseSummary,
  ExpenseSupplierOption,
  PaginatedResult,
} from '@optiflow/types';
import { api, withBranch } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';

export interface ExpenseListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  branchId?: string;
  categoryId?: string;
  supplierId?: string;
  status?: ExpenseStatus | '';
  method?: ExpensePaymentMethod | '';
  dateFrom?: string;
  dateTo?: string;
}

export interface ExpenseCategoryListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: boolean;
}

export interface ExpenseSupplierParams {
  page?: number;
  pageSize?: number;
  search?: string;
}

/**
 * Everything a posting decides.
 *
 * `branchId` is not part of the body — the API refuses one there. It travels as the branch context
 * for this one request, which is how a form can ask which branch is spending without switching the
 * branch the rest of the tab is working in.
 */
export interface PostExpenseBody {
  branchId: string;
  categoryId: string;
  expenseDate: string;
  amountMinor: number;
  method: ExpensePaymentMethod;
  externalReference?: string;
  supplierId?: string;
  payee?: string;
  notes?: string;
}

export const expenseKeys = {
  all: ['expenses'] as const,
  list: (params: ExpenseListParams) => [...expenseKeys.all, 'list', params] as const,
  detail: (id: string) => [...expenseKeys.all, 'detail', id] as const,
  categories: (params: ExpenseCategoryListParams) =>
    [...expenseKeys.all, 'categories', params] as const,
  category: (id: string) => [...expenseKeys.all, 'category', id] as const,
  suppliers: (params: ExpenseSupplierParams) => [...expenseKeys.all, 'suppliers', params] as const,
};

export function useExpensesQuery(
  params: ExpenseListParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: expenseKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<ExpenseSummary>>('/expenses', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
          ...(params.branchId ? { branchId: params.branchId } : {}),
          ...(params.categoryId ? { categoryId: params.categoryId } : {}),
          ...(params.supplierId ? { supplierId: params.supplierId } : {}),
          ...(params.status ? { status: params.status } : {}),
          ...(params.method ? { method: params.method } : {}),
          ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
          ...(params.dateTo ? { dateTo: params.dateTo } : {}),
        },
      }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useExpenseQuery(id: string | undefined) {
  return useQuery({
    queryKey: expenseKeys.detail(id ?? ''),
    queryFn: () => api.get<ExpenseDetail>(`/expenses/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

/**
 * The categories a tenant classifies its spending with.
 *
 * Reading needs `expenses.view` alone — anyone recording an expense has to fill the selector, and
 * asking them to hold the master-data capability for that would make the grant meaningless.
 */
export function useExpenseCategoriesQuery(
  params: ExpenseCategoryListParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: expenseKeys.categories(params),
    queryFn: () =>
      api.get<PaginatedResult<ExpenseCategorySummary>>('/expenses/categories', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
          ...(params.active === undefined ? {} : { active: params.active }),
        },
      }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/**
 * Suppliers an expense may name.
 *
 * Its own endpoint, not the supplier master: someone recording what the shop spent should not need
 * the contact book to say who was paid. It returns inactive suppliers too, because an expense is
 * often entered after one is retired.
 */
export function useExpenseSuppliersQuery(
  params: ExpenseSupplierParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: expenseKeys.suppliers(params),
    queryFn: () =>
      api.get<PaginatedResult<ExpenseSupplierOption>>('/expenses/suppliers', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 50,
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
        },
      }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

function invalidateExpenses(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: expenseKeys.all });
}

/**
 * Records an already-paid operating cost.
 *
 * One key per posting intent: a double click, or a retry after a lost response, spends once. It
 * creates no payable, so nothing in purchasing or payables is refreshed here — there would be
 * nothing to refresh.
 */
export function usePostExpenseMutation() {
  const queryClient = useQueryClient();

  return useIdempotentMutation<ExpenseDetail, PostExpenseBody>(
    ({ branchId, ...body }, config) =>
      api.post<ExpenseDetail>('/expenses', body, withBranch(branchId, config)),
    { onSuccess: () => invalidateExpenses(queryClient) },
  );
}

/** Stops OptiFlow counting an expense. It makes no claim that anyone got the money back. */
export function useVoidExpenseMutation() {
  const queryClient = useQueryClient();

  return useIdempotentMutation<ExpenseDetail, { id: string; reason: string }>(
    ({ id, reason }, config) =>
      api.post<ExpenseDetail>(`/expenses/${id}/void`, { reason }, config),
    { onSuccess: () => invalidateExpenses(queryClient) },
  );
}

export function useCreateExpenseCategoryMutation() {
  const queryClient = useQueryClient();

  return useIdempotentMutation<ExpenseCategorySummary, { name: string; nameAr?: string }>(
    (payload, config) =>
      api.post<ExpenseCategorySummary>('/expenses/categories', payload, config),
    { onSuccess: () => invalidateExpenses(queryClient) },
  );
}

/**
 * Renames a category at the version the screen showed.
 *
 * Not idempotent on purpose, and the same reasoning suppliers use: a repeat names a version that
 * has already moved on and is refused, which is exactly the answer someone editing a stale screen
 * should get.
 */
export function useUpdateExpenseCategoryMutation() {
  const queryClient = useQueryClient();

  return useIdempotentMutation<
    ExpenseCategorySummary,
    { id: string; name: string; nameAr?: string; version: number }
  >(
    ({ id, ...payload }) =>
      api.put<ExpenseCategorySummary>(`/expenses/categories/${id}`, payload),
    { onSuccess: () => invalidateExpenses(queryClient) },
  );
}

export function useExpenseCategoryActivationMutation() {
  const queryClient = useQueryClient();

  return useIdempotentMutation<
    ExpenseCategorySummary,
    { id: string; version: number; isActive: boolean }
  >(
    ({ id, version, isActive }) =>
      api.post<ExpenseCategorySummary>(
        `/expenses/categories/${id}/${isActive ? 'reactivate' : 'deactivate'}`,
        { version },
      ),
    { onSuccess: () => invalidateExpenses(queryClient) },
  );
}
